/**
 * Date and Time Utility Functions
 * 
 * Provides timezone-aware date handling for the transportation request system.
 * All functions use native JavaScript Date and Intl APIs (no external libraries).
 */

/**
 * Get the user's current timezone
 * @returns The IANA timezone identifier (e.g., "America/New_York")
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Combine separate date and time inputs into a UTC ISO 8601 string
 * 
 * Takes a date string (YYYY-MM-DD) and time string (HH:MM) in the user's local timezone
 * and converts them to a UTC ISO 8601 datetime string.
 * 
 * @param dateString - Date in YYYY-MM-DD format
 * @param timeString - Time in HH:MM format (24-hour)
 * @returns ISO 8601 datetime string in UTC (e.g., "2025-11-07T19:30:00.000Z")
 * @throws Error if date or time strings are invalid
 */
export const combineDateTime = (dateString: string, timeString: string): string => {
  if (!dateString || !timeString) {
    throw new Error('Both date and time are required');
  }

  // Validate date format (YYYY-MM-DD)
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) {
    throw new Error('Invalid date format. Expected YYYY-MM-DD');
  }

  // Validate time format (HH:MM)
  const timeRegex = /^\d{2}:\d{2}$/;
  if (!timeRegex.test(timeString)) {
    throw new Error('Invalid time format. Expected HH:MM');
  }

  // Combine date and time strings
  const dateTimeString = `${dateString}T${timeString}:00`;
  
  // Create Date object in local timezone
  const localDate = new Date(dateTimeString);
  
  // Check if date is valid
  if (isNaN(localDate.getTime())) {
    throw new Error('Invalid date or time value');
  }

  // Convert to UTC ISO string
  return localDate.toISOString();
};

/**
 * Format a UTC datetime string for display in the user's local timezone
 * 
 * Converts an ISO 8601 UTC datetime to a human-readable format in the user's local timezone.
 * Returns an object with separate date and time strings for flexible display.
 * 
 * @param isoDateTimeString - ISO 8601 datetime string (e.g., "2025-11-07T19:30:00.000Z")
 * @returns Object with formatted date, time, and timezone abbreviation
 * @example
 * formatFlightDateTime("2025-11-07T19:30:00.000Z")
 * // Returns: { date: "Friday, November 7, 2025", time: "2:30 PM", timezone: "EST" }
 */
export const formatFlightDateTime = (isoDateTimeString: string): {
  date: string;
  time: string;
  timezone: string;
} => {
  if (!isoDateTimeString) {
    throw new Error('DateTime string is required');
  }

  const date = new Date(isoDateTimeString);
  
  if (isNaN(date.getTime())) {
    throw new Error('Invalid datetime string');
  }

  // Format date as "Weekday, Month Day, Year"
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const formattedDate = dateFormatter.format(date);

  // Format time as "H:MM AM/PM"
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const formattedTime = timeFormatter.format(date);

  // Get timezone abbreviation (e.g., "EST", "PST")
  const timezoneFormatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  });
  const parts = timezoneFormatter.formatToParts(date);
  const timezonePart = parts.find(part => part.type === 'timeZoneName');
  const timezone = timezonePart ? timezonePart.value : '';

  return {
    date: formattedDate,
    time: formattedTime,
    timezone,
  };
};

/**
 * Validate that a date and time combination is in the future
 * 
 * Checks if the combined date/time is at least the specified number of hours in the future.
 * 
 * @param dateString - Date in YYYY-MM-DD format
 * @param timeString - Time in HH:MM format (24-hour)
 * @param minimumHoursAhead - Minimum hours in the future (default: 1)
 * @returns true if the datetime is in the future, false otherwise
 */
export const validateFutureDateTime = (
  dateString: string,
  timeString: string,
  minimumHoursAhead: number = 1
): boolean => {
  if (!dateString || !timeString) {
    return false;
  }

  try {
    // Combine date and time
    const dateTimeString = `${dateString}T${timeString}:00`;
    const selectedDate = new Date(dateTimeString);
    
    // Check if date is valid
    if (isNaN(selectedDate.getTime())) {
      return false;
    }

    // Get current time
    const now = new Date();
    
    // Calculate minimum required time (now + minimumHoursAhead)
    const minimumTime = new Date(now.getTime() + minimumHoursAhead * 60 * 60 * 1000);
    
    // Check if selected time is after minimum time
    return selectedDate >= minimumTime;
  } catch (error) {
    return false;
  }
};

/**
 * Get today's date in YYYY-MM-DD format for use as minimum date in date inputs
 * 
 * @returns Today's date in YYYY-MM-DD format
 */
export const getTodayDateString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse legacy date/time format for backward compatibility
 * 
 * Handles existing transportation requests that have separate flightDate and flightTime fields.
 * Combines them into a single datetime for consistent processing.
 * 
 * @param flightDate - Date string (YYYY-MM-DD)
 * @param flightTime - Time string (HH:MM)
 * @returns Object with formatted date, time, and timezone
 */
export const parseLegacyDateTime = (
  flightDate: string,
  flightTime: string
): {
  date: string;
  time: string;
  timezone: string;
} => {
  try {
    // Combine legacy fields and convert to ISO format
    const isoString = combineDateTime(flightDate, flightTime);
    // Format for display
    return formatFlightDateTime(isoString);
  } catch (error) {
    // Fallback for invalid legacy data
    return {
      date: flightDate || 'Invalid date',
      time: flightTime || 'Invalid time',
      timezone: '',
    };
  }
};

/**
 * Format a datetime for display in a compact format
 * 
 * @param isoDateTimeString - ISO 8601 datetime string
 * @returns Compact formatted string (e.g., "Nov 7, 2025 at 2:30 PM EST")
 */
export const formatCompactDateTime = (isoDateTimeString: string): string => {
  try {
    const { date, time, timezone } = formatFlightDateTime(isoDateTimeString);
    
    // Convert "Friday, November 7, 2025" to "Nov 7, 2025"
    const dateParts = date.split(', ');
    const monthDay = dateParts[1]; // "November 7"
    const year = dateParts[2]; // "2025"
    
    const [month, day] = monthDay.split(' ');
    const shortMonth = month.substring(0, 3); // "Nov"
    
    return `${shortMonth} ${day}, ${year} at ${time} ${timezone}`;
  } catch (error) {
    return 'Invalid date';
  }
};
